import { useCallback, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '@/constants/routes';
import type { FlashTone } from './useFlashMessage';
import type { MasterScreenState } from './useMasterScreenState';

/**
 * The record a master screen is holding, and everything the toolbar does to it.
 *
 * `useMasterScreenState` already owns which mode the screen is in. This owns the rest of what
 * every master file was declaring by hand: the loaded record, which tab is showing, the four busy
 * and dialog flags, the return-to-blank, and the six toolbar handlers — all of which were written
 * out, identically, eight times.
 *
 * What actually differs between screens is small and is what the options ask for: how to delete the
 * record, what to call it in the message afterwards, and what else the screen is holding that a
 * reset has to clear. Everything else is the same on all eight, so it lives here.
 *
 * Saving is deliberately not here. `executeSave` differs on every screen — different DTO, different
 * fields, sometimes a tree to reload — so the hook owns the confirm flag and the pending values,
 * and the screen owns the save itself.
 */

/** Deleting the record on screen. */
interface DeleteContract<TDto> {
  remove: (id: string) => Promise<unknown>;
  /** How the record is named in "X has been deleted." — usually its code. */
  describe: (record: TDto) => string;
  /** Anything to refresh afterwards, such as the cost centre tree. */
  after?: () => void | Promise<void>;
}

interface UseMasterRecordOptions<TDto, TTab extends string> {
  screen: MasterScreenState;
  flash: (tone: FlashTone, text: string) => void;
  /** Put the form back to blank — react-hook-form's `reset` with the screen's empty values. */
  clearForm: () => void;
  /** The tab a screen opens on, and returns to. */
  firstTab: TTab;
  /**
   * Focused shortly after a reset, so the next code can be typed without reaching for the mouse.
   * The delay is not cosmetic: the field is disabled while a record is loaded and cannot take
   * focus until React has re-rendered it enabled.
   */
  focusRef?: RefObject<HTMLInputElement>;
  /** Whatever the screen holds outside the form: code inputs, a chosen bank, a tree cursor. */
  clearScreen?: () => void;
  onDelete: DeleteContract<TDto>;
}

const FOCUS_AFTER_MS = 50;

export function useMasterRecord<TDto extends { id: string }, TTab extends string, TForm>({
  screen, flash, clearForm, firstTab, focusRef, clearScreen, onDelete,
}: UseMasterRecordOptions<TDto, TTab>) {
  const navigate = useNavigate();

  const [record, setRecord] = useState<TDto | null>(null);
  const [activeTab, setActiveTab] = useState<TTab>(firstTab);

  const [saving, setSaving] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const [pendingSave, setPendingSave] = useState<TForm | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /** Back to a blank screen, ready for the next code. */
  const resetToInitial = useCallback(() => {
    setRecord(null);
    clearForm();
    clearScreen?.();
    screen.setMode('initial');
    setActiveTab(firstTab);
    setPendingSave(null);
    setTimeout(() => focusRef?.current?.focus(), FOCUS_AFTER_MS);
  }, [clearForm, clearScreen, screen, firstTab, focusRef]);

  const executeDelete = useCallback(async () => {
    if (!record) return;
    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      await onDelete.remove(record.id);
      flash('success', `${onDelete.describe(record)} has been deleted.`);
      await onDelete.after?.();
      resetToInitial();
    } catch (err) {
      // Show what the server said. A refused delete is the one case where the message carries the
      // whole answer — which record is in the way, and whether to deactivate instead — and a bare
      // catch threw all of it away, leaving every master screen saying "try again" for something
      // retrying can never fix.
      const server = (err as { response?: { data?: { message?: string;
                        errors?: { message?: string }[] } } }).response?.data;
      flash('error',
        server?.message
        ?? server?.errors?.[0]?.message
        ?? 'Delete failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [record, onDelete, flash, resetToInitial]);

  /**
   * Ready to spread onto MasterToolbar, alongside `{...screen.toolbar}`.
   *
   * `onSave` is not here: it needs the screen's own `handleSubmit`, so the screen passes
   * `beginSave` through it.
   */
  const toolbar = {
    onRefresh: resetToInitial,
    onEdit:    () => screen.setMode('edit'),
    onPrint:   () => { if (record) setPrintOpen(true); },
    onDelete:  () => setShowDeleteConfirm(true),
    onExit:    () => navigate(AppRoutes.DASHBOARD),
  };

  /** Validated form values are parked here and the save is confirmed before anything is sent. */
  const beginSave = useCallback((values: TForm) => {
    setPendingSave(values);
    setShowSaveConfirm(true);
  }, []);

  return {
    record, setRecord,
    activeTab, setActiveTab,
    saving, setSaving,
    lookupBusy, setLookupBusy,
    printOpen, setPrintOpen,
    searchOpen, setSearchOpen,
    pendingSave, setPendingSave,
    showSaveConfirm, setShowSaveConfirm,
    showDeleteConfirm, setShowDeleteConfirm,
    resetToInitial,
    executeDelete,
    beginSave,
    toolbar,
  };
}

/**
 * The paged reader every master search modal wants, given that master's list endpoint.
 *
 * Seven screens wrote this out identically and only the api module differed.
 */
export function masterListFetcher<T>(
  getList: (filter: { search: string; pageNumber: number; pageSize: number }) =>
    Promise<{ data: { data: { items: T[]; totalCount: number } | null } }>,
) {
  return async (search: string, pageNumber: number, pageSize: number) => {
    const res = await getList({ search, pageNumber, pageSize });
    const page = res.data.data;
    return { items: page?.items ?? [], totalCount: page?.totalCount ?? 0 };
  };
}
